import '@mobiscroll/react/dist/css/mobiscroll.scss'
import '@mobiscroll/react/dist/css/mobiscroll-legacy.scss'
import './Calendar.css'

import {
  Eventcalendar,
  CalendarNav,
  CalendarPrev,
  CalendarNext,
} from '@mobiscroll/react'
import { format } from 'date-fns'
import isEmpty from 'lodash.isempty'
import type { PropsWithChildren } from 'react'

import { type SelectedTile } from 'Components/CalendarModal'

import CalendarTile, { type CalendarTileProps } from './CalendarTile'

// Cap on cards shown per day cell; the remainder collapses into an "N more"
// trigger that opens the agenda pane (Figma: uniform cells + overflow).
const MAX_VISIBLE_TILES = 2

export interface CalendarEvent {
  id: string
  start: string
  end: string
  tileData: CalendarTileProps
  isReadOnly: boolean
}

export interface CalendarProps {
  defaultSelectedDate?: Date
  events: CalendarEvent[]
  tileClick: (selectedTile: SelectedTile) => void
  onSelectedDateChange?: (changedDate: Date) => void
  onShowMore?: (date: Date, events: CalendarEvent[]) => void
  /** Rendered at the left of the month-nav header row (e.g. the legend). */
  legend?: React.ReactNode
  isReadOnly: boolean
}

const toSelectedTile = (event: CalendarEvent): SelectedTile => ({
  id: event.id.toString(),
  date: format(new Date(event.start), 'MMM d yyyy'),
  time: event.tileData.time,
  platform: event.tileData.platform,
  status: event.tileData.status,
})

function Calendar({
  defaultSelectedDate,
  events,
  tileClick,
  onSelectedDateChange,
  onShowMore,
  legend,
  isReadOnly,
}: PropsWithChildren<CalendarProps>) {
  return (
    <div className="ext-calendar mx-auto max-w-[1280px] px-4 pt-8 md:px-10">
      <Eventcalendar
        theme="ios-legacy"
        defaultSelectedDate={defaultSelectedDate || new Date()}
        onSelectedDateChange={(args) => {
          onSelectedDateChange?.(args.date as Date)
        }}
        data={events}
        themeVariant="light"
        view={{
          calendar: {
            type: 'month',
            labels: 'all',
            outerDays: false,
          },
        }}
        height={820}
        renderHeader={() => (
          <div className="flex w-full items-center justify-between gap-4">
            {legend}
            <div className="ext-calendar-nav flex items-center gap-1">
              <div data-testid="previous-month">
                <CalendarPrev />
              </div>
              <CalendarNav />
              <div data-testid="next-month">
                <CalendarNext />
              </div>
            </div>
          </div>
        )}
        renderCalendarDayContent={({ events: dayEventsRaw, date }) => {
          const dayEvents = ((dayEventsRaw ?? []) as CalendarEvent[]).filter(
            (e) => !isEmpty(e.tileData)
          )

          if (dayEvents.length === 0) return null

          const visible = dayEvents.slice(0, MAX_VISIBLE_TILES)
          const overflow = dayEvents.length - visible.length

          return (
            <div className="relative z-[2] mx-auto mb-2 mt-0.5 flex flex-col gap-1 px-1">
              {visible.map((e) => (
                <CalendarTile
                  key={e.id}
                  {...e.tileData}
                  data-testid={`tile-${e.id}`}
                  onClick={() => {
                    if (e.id) tileClick(toSelectedTile(e))
                  }}
                  isReadOnly={isReadOnly}
                />
              ))}

              {overflow > 0 && date && (
                <button
                  type="button"
                  data-testid={`show-more-${format(date as Date, 'yyyy-MM-dd')}`}
                  className="flex cursor-pointer items-center justify-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
                  onClick={() => onShowMore?.(date as Date, dayEvents)}
                >
                  {overflow} more
                </button>
              )}
            </div>
          )
        }}
      />
    </div>
  )
}

export default Calendar
